import { Button } from '@ambernord/ui';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppHeader } from '../components/AppHeader.js';
import { mfaVerify } from '../lib/api.js';
import { playAudioCue } from '../lib/audio.js';
import MfaEnrollment from './MfaEnrollment.js';

type Mode = 'totp' | 'recovery';

const TOTP_LENGTH = 6;
const RECOVERY_MIN_LENGTH = 8;

/** Pieslēgšanās laika MFA: 6 ciparu TOTP vai (pārslēdzot) vienreizējs rezerves kods. */
function MfaChallenge() {
  const { t } = useTranslation('auth');
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isTotp = mode === 'totp';
  const ready = isTotp
    ? code.length === TOTP_LENGTH
    : code.replace(/[\s-]/g, '').length >= RECOVERY_MIN_LENGTH;

  function switchMode() {
    setMode(isTotp ? 'recovery' : 'totp');
    setCode('');
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await mfaVerify(code.trim());
      if (!res.ok || !res.data.ok) {
        const invalid = res.data.error === 'invalid_code' || res.data.error === 'invalid_input';
        setError(invalid ? t('mfa.invalidCode') : (res.data.error ?? t('status.error')));
        playAudioCue('error');
        return;
      }
      playAudioCue('success');
      await refresh();
      navigate('/', { replace: true });
    } catch {
      setError(t('status.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <AppHeader />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-10">
        <section className="rounded-lg border border-slate-200 p-6 dark:border-slate-800">
          <h2 className="mb-1 text-base font-medium">{t('mfa.heading')}</h2>
          <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
            {isTotp ? t('mfa.description') : t('mfa.recoveryDescription')}
          </p>
          <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium" htmlFor="code">
                {isTotp ? t('mfa.code') : t('mfa.recoveryCode')}
              </label>
              {isTotp ? (
                <input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={TOTP_LENGTH}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="h-10 rounded-md border border-slate-300 bg-white px-3 text-center text-lg tracking-widest dark:border-slate-700 dark:bg-slate-900"
                />
              ) : (
                <input
                  id="code"
                  inputMode="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  maxLength={16}
                  placeholder="XXXX-XXXX"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="h-10 rounded-md border border-slate-300 bg-white px-3 text-center font-mono text-lg uppercase tracking-widest dark:border-slate-700 dark:bg-slate-900"
                />
              )}
            </div>
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" disabled={submitting || !ready}>
              {submitting ? t('status.loading') : t('mfa.submit')}
            </Button>
            <button
              type="button"
              onClick={switchMode}
              className="self-center text-sm text-amber-600 hover:underline dark:text-amber-400"
              data-testid="mfa-toggle-mode"
            >
              {isTotp ? t('mfa.useRecovery') : t('mfa.useAuthenticator')}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}

export default function Mfa() {
  const { user } = useAuth();
  return user?.mfaSetupRequired ? <MfaEnrollment /> : <MfaChallenge />;
}
